import {
  CategoryCard,
  ListingCategoryType,
  NavMenuCategory} from 'modules/products'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

export const getFlattenCategories = <T extends { subCategories?: T[] }>(
  categories: T[]
): T[] => {
  const result: T[] = []

  const traverseCategories = (category: T) => {
    result.push(category)
    if ((category.subCategories?.length || 0) > 0) {
      category.subCategories?.forEach((subCategory: T) => {
        traverseCategories(subCategory)
      })
    }
  }

  categories?.forEach((category: T) => {
    traverseCategories(category)
  })

  return result
}

export const findCategoryBySlug = (
  categories: ListingCategoryType[] = [],
  categorySlug: string | undefined
): ListingCategoryType | null => {
  const flattenCategories = getFlattenCategories(categories)

  return flattenCategories?.find(c => c.slug === categorySlug) || null
}

interface FindMainCategoryParams<T> {
  depth?: number
  parentId?: string | null
  id: string
  subCategories?: T[]
}

export const findMainCategory = <T extends FindMainCategoryParams<T>>(
  categories: T[],
  currentCategory: T | null
): T | null => {
  const list = getFlattenCategories(categories)

  const find = (category: T | null): T | null => {
    if (!category) return null

    if (category.depth !== 1) {
      const parent = list.find(cat => cat.id === category.parentId)

      if (parent) {
        return find(parent)
      } else {
        return null
      }
    } else {
      return list?.find(cat => cat.id === category.id) || null
    }
  }

  return find(currentCategory)
}

export const filterOnlyVisibleCategories = (
  categories: ListingCategoryType[]
): ListingCategoryType[] => {
  const filterSubCategories = (
    filteredCategories: ListingCategoryType[]
  ): ListingCategoryType[] =>
    filteredCategories
      .map(category => ({
        ...category,
        subCategories:
          category.subCategories
            ?.map(subCategory => ({
              ...subCategory,
              subCategories:
                subCategory.subCategories?.filter(
                  subSubCategory => subSubCategory.isVisibleFlag
                ) || []
            }))
            .filter(subCategory => subCategory.isVisibleFlag) || []
      }))
      .filter(category => category.isVisibleFlag)

  return filterSubCategories(categories)
}

export const isActive = <T extends { totalSlug: string; subCategories?: T[] }>(
  pathname: string,
  category: T
): boolean => {
  if (pathname === category.totalSlug) {
    return true
  }

  if (isNotNilOrEmpty(category.subCategories)) {
    return !!category.subCategories?.some(subCategory =>
      isActive(pathname, subCategory)
    )
  }

  return false
}

const toNavMenuCategoriesAtDepth = (
  categories: ListingCategoryType[] | undefined,
  depth: number
): NavMenuCategory[] =>
  (categories ?? [])
    .filter(category => category.isVisible)
    .map(category => {
      const subCategories = toNavMenuCategoriesAtDepth(
        category.subCategories,
        depth + 1
      )

      return {
        id: category.id,
        name: category.name,
        totalSlug: category.totalSlug,
        parentId: category.parentId,
        depth,
        isVisible: category.isVisible,
        productsCount: category.productsCount,
        ...(depth === 1 && category.icon?.path
          ? { icon: { path: category.icon.path } }
          : {}),
        ...(subCategories.length ? { subCategories } : {})
      }
    })

export const toNavMenuCategories = (
  categories: ListingCategoryType[]
): NavMenuCategory[] => toNavMenuCategoriesAtDepth(categories, 1)

/** Slim listing categories for the homepage CategoriesSection — only fields actually rendered. */
export const toVisibleListingCategories = (
  categories: ListingCategoryType[]
): CategoryCard[] =>
  categories
    .filter(c => c.isVisible)
    .map(({ id, name, totalSlug, type, image }) => ({
      id,
      name,
      totalSlug,
      type,
      image: image ? { path: image.path } : null
    }))
